import React, { Component, Children } from 'react';
import PropTypes from 'prop-types';
import { replace } from 'react-router-redux';
import qs from 'qs';
import { flashErrorMessage } from '../../../actions/flash';
import { fetchM } from '../../../actions/fetchM';
import { checkChanges } from '../../../utils/checkChanges';
import { decodeSearch, encodeSearch } from '../../../utils/url';
import isNumber from '../../../utils/numbers/isNumber';
import {
  NEW_IMPORT_PROMPT,
  NEW_PROJECT_UNIT_PROMPT,
} from '../../../constants/promptMessages';

import List from './List';
import New from './New';
import Import from './Import';
import ImportPricing from './ImportPricing';
import UnitsCompare from './UnitsCompare';
import HIMSUnitSync from './HIMSUnitSync/HIMSUnitSync';

import '../../../styles/containers/project/units/unitsCompare.less';

export default class Project extends Component {
  static propTypes = {
    params: PropTypes.shape({
      id: PropTypes.string,
      unitID: PropTypes.string,
    }),
    data: PropTypes.shape({
      name: PropTypes.string,
    }),
    children: PropTypes.node,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
      query: PropTypes.object,
    }),
  }
  static contextTypes = {
    store: PropTypes.object,
  }

  static List = List
  static Import = checkChanges(Import, { message: NEW_IMPORT_PROMPT })
  static ImportPricing = checkChanges(ImportPricing, { message: NEW_IMPORT_PROMPT })
  static New = checkChanges(New, { message: NEW_PROJECT_UNIT_PROMPT })
  static UnitsCompare = UnitsCompare
  static HIMSUnitSync = HIMSUnitSync

  constructor(props) {
    super(props);
    this.state = {
      count: 0,
      data: [],
      limit: 10,
      offset: 0,
      token: null,
      loading: true,
      updating: false,
    };
  }
  componentDidMount() {
    const query = decodeSearch(this.props.location.search);
    if (this.props.location.pathname.includes('/create/')) {
      this.fetchProjectUnits();
    } else if (this.props.location.pathname.includes('/import/')) {
      this.fetchProjectUnits();
      this.fetchImports(query);
      this.ticker = setInterval(() => {
        if (this.state.updating) {
          this.fetchImports();
        }
      }, 2000);
    } else if (this.props.location.pathname.includes('/import_pricings/')) {
      this.fetchProjectUnits();
      this.fetchPricingImports(query);
    } else {
      this.setLoading(false);
    }
  }
  componentWillReceiveProps(nextProps) {
    const { pathname } = nextProps.location;
    const query = decodeSearch(nextProps.location.search);
    if (pathname.includes('/create/')) {
      this.fetchProjectUnits();
    } else if (pathname.includes('/import/')) {
      this.fetchProjectUnits();
      this.fetchImports(query);
    } else if (pathname.includes('/import_pricings/')) {
      this.fetchProjectUnits();
      this.fetchPricingImports(query);
    }
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  setUpdating = (update) => {
    this.setState({ updating: update });
  }
  async fetchProjectUnits(q = {}) {
    await this.setLoading(true);
    const { store } = this.context;
    const query = Object.assign({
      page: 1,
      limit: 50,
    }, q);
    let res;
    try {
      res = await store.dispatch(fetchM(`/projects/${this.props.params.id}/units/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const token = res.headers.get('X-Csrf-Token');
      // handle token.. etc
      await this.setComponentState({
        token,
      });
    }
    await this.setLoading(false);
  }
  async fetchImports(q) {
    await this.setLoading(true);
    const { store } = this.context;
    const query = Object.assign({}, q);
    if (!query.page || !isNumber(query.page) || query.page <= 0) {
      query.page = 1;
    }
    query.projectID = this.props.params.id;
    let res;
    try {
      res = await store.dispatch(fetchM(`/data_imports/units/?${encodeSearch(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const count = parseInt(res.headers.get('M-Data-count'), 10);
      const limit = parseInt(res.headers.get('M-Data-limit'), 10);
      const offset = parseInt(res.headers.get('M-Data-offset'), 10);
      const data = await res.json();
      let isUpdating = this.state.updating;
      if (data && data[0]) {
        if (!isUpdating && data[0].statusID === 'in_progress') isUpdating = true;
        if (data[0].statusID === 'completed' || data[0].statusID === 'failed') isUpdating = false;
      }
      if (this.state.data && this.state.data.length < 1 && query.page > 1) {
        query.page = 1;
        store.dispatch(replace(`/projects/${this.props.params.id}/units/import/?${encodeSearch(query)}`));
      }
      // handle token.. etc
      await this.setComponentState({
        count,
        data,
        limit,
        offset,
        updating: isUpdating,
      });
    }
    await this.setLoading(false);
  }
  async fetchPricingImports(q) {
    await this.setLoading(true);
    const { store } = this.context;
    const query = Object.assign({}, q);
    if (!query.page || !isNumber(query.page) || query.page <= 0) {
      query.page = 1;
    }
    query.projectID = this.props.params.id;
    let res;
    try {
      res = await store.dispatch(fetchM(`/data_imports/pricings/?${encodeSearch(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const count = parseInt(res.headers.get('M-Data-count'), 10);
      const limit = parseInt(res.headers.get('M-Data-limit'), 10);
      const offset = parseInt(res.headers.get('M-Data-offset'), 10);
      const data = await res.json();
      if (this.state.data && this.state.data.length < 1 && query.page > 1) {
        query.page = 1;
        store.dispatch(replace(`/projects/${this.props.params.id}/units/import/?${encodeSearch(query)}`));
      }
      // handle token.. etc
      await this.setComponentState({
        count,
        data,
        limit,
        offset,
      });
    }
    await this.setLoading(false);
  }
  forceFetchImports = () => {
    this.fetchImports();
  }
  forceFetchPricingImports = () => {
    this.fetchPricingImports();
  }
  renderChild() {
    return Children.map(this.props.children, child => (
      React.cloneElement(child, {
        count: this.state.count,
        data: this.state.data,
        limit: this.state.limit,
        offset: this.state.offset,
        token: this.state.token,
        loading: this.state.loading,
        id: this.props.params.id,
        unitID: this.props.params.unitID,
        location: this.props.location,
        project: this.props.data,
        fetchImports: this.forceFetchImports,
        fetchPricingImports: this.forceFetchPricingImports,
        setUpdating: this.setUpdating,
        updating: this.state.updating,
      })
    ));
  }
  render() {
    return (
      <div>
        {this.renderChild()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/containers/Project/Units/Units.jsx